import { useState } from "react";
import {
  Button,
  ErrorMessage,
  Flex,
  InfoBox,
  Label,
  LoadingIndicator,
  ModalV2,
  Select,
  Text,
  Textarea,
} from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import * as styles from "./styles_vanilla.css";
import type { ExtractionDefaults } from "./useStructureDdqRun";

const PROMPT_FIELD_ID = "ddq-structure-prompt";

export type StructureDdqModalProps = {
  defaults: ExtractionDefaults | undefined;
  isLoadingDefaults: boolean;
  errorMessage: string | null;
  isSubmitting: boolean;
  completedDdqTitle?: string | null;
  onHide: () => void;
  onConfirm: (questionsPrompt: string) => void;
};

// Mounted only while open, so the prompt and its lock reset between runs.
export const StructureDdqModal = ({
  defaults,
  isLoadingDefaults,
  errorMessage,
  isSubmitting,
  completedDdqTitle = null,
  onHide,
  onConfirm,
}: StructureDdqModalProps) => {
  const [editedPrompt, setEditedPrompt] = useState<string | null>(null);
  const [isPromptEditable, setIsPromptEditable] = useState(false);

  const serverPrompt = defaults?.questionsPrompt ?? "";
  const prompt = editedPrompt ?? serverPrompt;
  const selectedModel =
    defaults?.models.find((model) => model.isDefault) ?? defaults?.models[0];

  return (
    <ModalV2 show onHide={onHide} title="Structure DDQ" data-testid="ddq-structure-modal">
      <ModalV2.Body>
        {isLoadingDefaults ? (
          <LoadingIndicator data-testid="ddq-structure-defaults-loading" />
        ) : (
          <Flex flexDirection="column" gap={SPACING.LARGE}>
            <InfoBox
              variant="info"
              title="Re-running this job will overwrite the existing structure."
            />
            {completedDdqTitle != null && (
              <div>
                <Label>Completed DDQ</Label>
                <Text variant="regular-md" data-testid="ddq-structure-completed-ddq-file">
                  {completedDdqTitle}
                </Text>
              </div>
            )}
            <Select
              full
              inModal
              hideScrollBar
              hasLeftPadding={false}
              disabled
              label="Select model"
              value={selectedModel?.id ?? ""}
              helpText="Model selection arrives with DDQ-227."
              data-testid="ddq-structure-model-select"
            >
              <Select.Section>
                {(defaults?.models ?? []).map((model) => (
                  <Select.Option key={model.label} value={model.id ?? ""}>
                    {model.label}
                  </Select.Option>
                ))}
              </Select.Section>
            </Select>
            <div>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                className={styles.promptLabelRow}
              >
                <Label htmlFor={PROMPT_FIELD_ID}>Prompt</Label>
                <Button
                  variant="link"
                  displayAsLink
                  isInline
                  childAsIcon={{ type: "pen-to-square" }}
                  className={styles.editPromptButton}
                  aria-label="Edit prompt"
                  disabled={isPromptEditable}
                  onClick={() => setIsPromptEditable(true)}
                  data-testid="ddq-structure-edit-prompt"
                />
              </Flex>
              <Textarea
                isFullWidth
                size="large"
                id={PROMPT_FIELD_ID}
                value={prompt}
                readOnly={!isPromptEditable}
                onSetValue={setEditedPrompt}
                data-testid="ddq-structure-prompt"
              />
            </div>
            {errorMessage && (
              <ErrorMessage data-testid="ddq-structure-error">{errorMessage}</ErrorMessage>
            )}
          </Flex>
        )}
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex flexDirection="row" gap={SPACING.SMALL} justifyContent="flex-end">
          <Button variant="neutral" onClick={onHide}>
            Cancel
          </Button>
          <Button
            isLoading={isSubmitting}
            disabled={isLoadingDefaults || !prompt}
            onClick={() => onConfirm(prompt)}
            data-testid="ddq-structure-confirm"
          >
            {completedDdqTitle != null ? "Process" : "Start structure"}
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
